import React, { useState, useEffect, useRef, useCallback, memo, } from 'react';
import { Jumper } from 'tea-component';
import './index.scss';

// '#5086F3', '#77E097', '#F8CC6A'
const COLORS = ['80, 134, 243', '119, 224, 151', '248, 204, 106'];

interface GeneralLegendProps {
  className?: string,
  optList: {bgColor?: string, text: string | JSX.Element | JSX.Element[], icon?: any}[];
  [keyName: string]: any;
}

const DEFAULT_PROPS: GeneralLegendProps = {
  className: '',
  optList: [],
};

const GeneralLegend = (props: GeneralLegendProps) => {
  const {
    className,
    optList,
  } = props;

  const [page, setPage] = useState<number>(1);
  const [maxPage, setMaxPage] = useState<number>(1);
  const [visible, setVisible] = useState<boolean>(false);
  const [allItemWidth, setAllItemWidth] = useState<number[]>([]);
  const [lastItemWidth, setLastItemWidth] = useState<number>(0);
  const legendContainerRef = useRef<HTMLElement>(null);
  const legendContentRef = useRef<HTMLElement>(null);

  const handleResize = useCallback((widthArr) => {
    if (!legendContainerRef?.current || !legendContentRef?.current) return;
    const containerWidth = legendContainerRef?.current?.offsetWidth,
          contentWidth = legendContentRef?.current?.offsetWidth,
          sumItemWidth = widthArr?.reduce((o: number, t: number) => { o += t; return o; }, 0); // 通过相加所有item宽度计算出的contentWidth
    if (contentWidth > containerWidth || sumItemWidth > containerWidth) {
      const count = Math.ceil(contentWidth / (containerWidth - 70)); // 70为分页器占的位置
      setMaxPage(count);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, []);

  // 屏幕缩放导致页码减少，page>maxPage的情况
  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [maxPage]);

  useEffect(() => {
    const legendContainer = document.getElementById('gp-legend-container');
    const legendContent = document.getElementById('gp-legend-content');
    // @ts-ignore
    legendContainerRef.current = legendContainer;
    // @ts-ignore
    legendContentRef.current = legendContent;
    // 拿到所有子item的宽度
    const childNodes: any = legendContent?.childNodes;
    const _allItemWidth: number[] = [];
    for (let i = 0; i < childNodes?.length; i++) {
      const t = childNodes?.[i];
      _allItemWidth?.push(t?.offsetWidth);
    }
    setAllItemWidth(_allItemWidth);
    // 最后一个item的宽度
    setLastItemWidth(_allItemWidth?.[_allItemWidth?.length - 1]);

    const fn = () => handleResize(_allItemWidth);
    window.addEventListener('resize', fn);
    handleResize(_allItemWidth);

    return () => {
      window.removeEventListener('resize', fn);
    }
  }, []);

  const jumperProps = {
    prevDisabled: page === 1,
    nextDisabled: page === maxPage,
    onNext: () => {
      if (!legendContainerRef.current || !legendContentRef.current) return;
      // 当前看见宽度
      const showWidth = legendContainerRef.current?.offsetWidth - 70;
      // 最后一页显示内容宽度
      const lastShowWidth = legendContentRef.current?.offsetWidth - showWidth * (maxPage - 1);
      // 当显示最后一页且最后一个显示不全时，显示全最后一个
      if (lastItemWidth > lastShowWidth && page === maxPage - 1) {
        legendContentRef.current.style.marginLeft = `-${page * showWidth - (lastItemWidth - lastShowWidth)}px`;
      } else {
        legendContentRef.current.style.marginLeft = `-${page * showWidth}px`;
      }
      setPage(page + 1);
    },
    onPrev: () => {
      if (!legendContainerRef.current || !legendContentRef.current) return;
      legendContentRef.current.style.marginLeft = `-${(page - 2) * (legendContainerRef.current?.offsetWidth - 70)}px`;
      setPage(page - 1);
    },
  };

  return (
    <div className={`gp-general-legend ${className}`} id="gp-legend-container">
      <div
        className="gp-general-legend-content"
        id="gp-legend-content"
        style={{width: visible ? 'max-content' : 'auto'}}
      >
        {visible && (
          <div className="custom-pagination">
            <div className="current-page">{`${page}/${maxPage}`}</div>
            <Jumper noBordered {...jumperProps} />
          </div>
        )}
        {optList?.map((t: {bgColor?: string, text: string | JSX.Element | JSX.Element[], icon?: any}, i: number) => {
          return (
            <div
              key={i}
              className={`gp-general-legend-item ${visible ? 'is-pagination-item' : ''}`}
            >
              {t?.icon || (
                <div
                  className="gp-general-legend-circular"
                  style={{
                    border: `1px solid ${t?.bgColor?.includes('#') ? t?.bgColor : `rgba(${t?.bgColor || COLORS?.[i] || '0, 0, 0'}, 1)`}`,
                    backgroundColor: `${t?.bgColor?.includes('#') ? t?.bgColor : `rgba(${t?.bgColor || COLORS?.[i] || '0, 0, 0'}, 1)`}`,
                  }}
                ></div>
              )}
              <div style={{whiteSpace: 'nowrap'}}>{t?.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

GeneralLegend.defaultProps = DEFAULT_PROPS;

export default memo(GeneralLegend);
